import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import api from '../api';
import { getErrorMessage, PublicUser } from '../types';
import { useTranslation } from '../i18n';
import { ProductList } from '../components/ProductList';
import { LevelBadge } from '../components/LevelBadge';
import { StarRating } from '../components/StarRating';
import { LocationMap } from '../components/LocationMap';
import { ReportContentButton } from '../components/ReportContentButton';

interface UserPageProps {
  token: string | null;
  userId: number | null;
  isVerified: boolean;
  onLogin: (accessToken: string, refreshToken: string) => void;
  setError: (e: string) => void;
}

export const UserPage: React.FC<UserPageProps> = ({
  token,
  userId,
  isVerified,
  onLogin,
  setError,
}) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState('');

  const numericId = id != null && /^\d+$/.test(id) ? Number(id) : null;

  const load = useCallback(async () => {
    if (numericId == null) return;
    setLoading(true);
    setLoadError('');
    setNotFound(false);
    try {
      const res = await api.get<PublicUser>(`/users/public/${numericId}`);
      setProfile(res.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNotFound(true);
        setProfile(null);
      } else {
        setLoadError(getErrorMessage(err, t('user.load_error')));
      }
    } finally {
      setLoading(false);
    }
  }, [numericId, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (numericId == null) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return <p className="text-muted">{t('user.loading')}</p>;
  }

  if (notFound) {
    return <p className="text-muted">{t('user.not_found')}</p>;
  }

  if (loadError || !profile) {
    return (
      <div className="alert alert-error">
        {loadError || t('user.load_error')}
      </div>
    );
  }

  const hasLocation =
    profile.approximateLatitude != null && profile.approximateLongitude != null;

  return (
    <div>
      <div className="user-profile-header">
        <div className="user-profile-header__identity">
          <h1 className="user-profile-header__name">{profile.username}</h1>
          <LevelBadge level={profile.level} size="md" />
          <span
            className={`user-profile-header__verified${profile.isVerified ? '' : ' user-profile-header__verified--no'}`}
          >
            {profile.isVerified ? t('user.verified') : t('user.not_verified')}
          </span>
        </div>
        <div className="user-profile-header__rating">
          {profile.ratingCount > 0 ? (
            <>
              <StarRating value={profile.averageRating} size="sm" />
              <span className="text-muted">
                {profile.averageRating.toFixed(1)} ({profile.ratingCount})
              </span>
            </>
          ) : (
            <span className="text-muted">{t('user.rating_none')}</span>
          )}
        </div>
        {hasLocation && (
          <div className="user-profile-header__location">
            <p className="text-muted" style={{ fontSize: '0.9rem', margin: '0 0 0.35rem' }}>
              {t('user.location')}
            </p>
            <LocationMap
              approximate={{
                lat: profile.approximateLatitude!,
                lng: profile.approximateLongitude!,
              }}
              height={220}
              interactive={false}
            />
          </div>
        )}
        {profile.id !== userId && (
          <div style={{ marginTop: '0.75rem' }}>
            <ReportContentButton reportedUserId={profile.id} />
          </div>
        )}
      </div>

      <h2 className="section-title">
        {t('user.items_title', { username: profile.username })}
      </h2>
      <ProductList
        ownerId={numericId}
        token={token}
        userId={userId}
        isVerified={isVerified}
        onLogin={onLogin}
        setError={setError}
      />
    </div>
  );
};
